import { AppError, ErrorCode, ErrorSeverity } from './types';

let lastSent = 0;

// High-security data sanitization
const SENSITIVE_PATTERNS = [
  // Firebase/Firestore session and request IDs
  /SID=[a-zA-Z0-9\s\-_]+/gi,
  /RID=\d+/gi,
  /channel[a-zA-Z0-9]+/gi,
  /session[a-zA-Z0-9]+/gi,
  // API keys and tokens
  /api[_-]?key[=:]\s*[a-zA-Z0-9\-_]+/gi,
  /token[=:]\s*[a-zA-Z0-9\-_.]+/gi,
  /bearer\s+[a-zA-Z0-9\-_.]+/gi,
  // Email addresses (partial masking)
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  // Phone numbers
  /\+?[1-9]\d{1,14}/g,
  // Credit card numbers
  /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,
  // Firebase project IDs and other identifiers
  /projects\/[a-zA-Z0-9\-_]+\//gi,
  /databases\/[a-zA-Z0-9\-_]+\//gi,
];

function sanitizeData(data: any): any {
  if (typeof data === 'string') {
    let sanitized = data;
    SENSITIVE_PATTERNS.forEach(pattern => {
      if (pattern.source.includes('email')) {
        // Mask email addresses: user@domain.com -> u***@d***.com
        sanitized = sanitized.replace(pattern, (match) => {
          const [local, domain] = match.split('@');
          const maskedLocal = local.length > 1 ? local[0] + '*'.repeat(local.length - 1) : local;
          const [domainName, tld] = domain.split('.');
          const maskedDomain = domainName.length > 1 ? domainName[0] + '*'.repeat(domainName.length - 1) : domainName;
          return `${maskedLocal}@${maskedDomain}.${tld}`;
        });
      } else if (pattern.source.includes('phone')) {
        // Mask phone numbers: +1234567890 -> +123****890
        sanitized = sanitized.replace(pattern, (match) => {
          if (match.length > 6) {
            return match.slice(0, 3) + '*'.repeat(match.length - 6) + match.slice(-3);
          }
          return '*'.repeat(match.length);
        });
      } else {
        // Replace other sensitive patterns with [REDACTED]
        sanitized = sanitized.replace(pattern, '[REDACTED]');
      }
    });
    return sanitized;
  }
  
  if (Array.isArray(data)) {
    return data.map(sanitizeData);
  }
  
  if (data && typeof data === 'object') {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(data)) {
      // Skip sensitive keys entirely
      if (key.toLowerCase().includes('password') || 
          key.toLowerCase().includes('secret') || 
          key.toLowerCase().includes('token') || 
          key.toLowerCase().includes('key') ||
          key.toLowerCase().includes('sid') ||
          key.toLowerCase().includes('rid')) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitizeData(value);
      }
    }
    return sanitized;
  }
  
  return data;
}

function extractFirestorePath(cause: any): string | undefined {
  if (!cause) return undefined;
  // FirebaseError sometimes carries customData with resource info
  const customPath = cause?.customData?.path || cause?.customData?.refPath;
  if (typeof customPath === 'string' && customPath.trim().length > 0) {
    return customPath;
  }

  // Try to parse from message (e.g., contains /documents/<path>)
  if (typeof cause?.message === 'string') {
    const match = cause.message.match(/documents\/([^\s"'`]+)/i);
    if (match?.[1]) return match[1];
  }

  // As a last resort, attempt to parse from stack trace
  if (typeof cause?.stack === 'string') {
    const match = cause.stack.match(/documents\/([^\s"'`]+)/i);
    if (match?.[1]) return match[1];
  }

  return undefined;
}

function logPermissionDeniedDebug(e: AppError) {
  try {
    const cause: any = e.cause;
    const firestorePath = extractFirestorePath(cause);
    const debugPayload = {
      firestorePath: firestorePath || 'unknown',
      firebaseCode: cause?.code ?? 'unknown',
      firebaseMessage: cause?.message ?? 'Missing or insufficient permissions',
      ctx: e.ctx,
      route: typeof window !== 'undefined' ? window.location.pathname + window.location.search : 'n/a',
      stack: cause?.stack,
      hint: 'Check Firestore rules for the path above. Ensure read/write rules allow this user/operation.',
    };

    // Store the last permission error for quick inspection in devtools
    if (typeof window !== 'undefined') {
      (window as any).__LAST_FIRESTORE_PERMISSION_ERROR__ = debugPayload;
    }

    secureLog('error', '[Firestore Rules] Permission denied', debugPayload);
  } catch (loggingError) {
    secureLog('warn', 'Failed to log permission-denied debug info', loggingError);
  }
}

// SECURITY: Secure logging function that only logs in development
// All data is sanitized before logging to prevent information leakage
function secureLog(level: 'log' | 'warn' | 'error' | 'info', message: string, data?: any) {
  const sanitizedMessage = sanitizeData(message);
  const sanitizedData = data ? sanitizeData(data) : undefined;
  
  // SECURITY: Only log in development or when explicitly enabled
  // In production, this function does nothing to prevent information leakage
  if (process.env.NODE_ENV === 'development' || process.env.VITE_ENABLE_DEBUG_LOGS === 'true') {
    if (sanitizedData) {
      console[level](sanitizedMessage, sanitizedData);
    } else {
      console[level](sanitizedMessage);
    }
  }
  // SECURITY: In production, all console logging is disabled
}
export function logError(e: AppError) {
  // Filter out common expected errors that don't need logging
  const shouldSuppress = [
    'DB_INDEX_REQUIRED',
    'TRANSIENT_BACKEND_UNAVAILABLE',
    'DB_UNAVAILABLE',
    'FUNC_UNAVAILABLE',
    'UNKNOWN'
  ];
  
  if (shouldSuppress.includes(e.code)) {
    // Only log suppressed errors when debug flag is enabled, or if they're fatal
    const shouldLog = process.env.NODE_ENV === 'development' && 
                      (process.env.VITE_ENABLE_DEBUG_LOGS === 'true' || e.severity === ErrorSeverity.Fatal);
    
    if (shouldLog) {
      if (e.severity === ErrorSeverity.Fatal) {
        secureLog('warn', '[AppError] Suppressed (Fatal):', { 
          code: e.code, domain: e.domain, message: e.message 
        });
      } else {
        secureLog('warn', '[AppError] Suppressed:', {
          code: e.code, domain: e.domain, message: e.message
        });
      }
    }
    return;
  }

  // Use secure logging to sanitize sensitive data for non-suppressed errors
  secureLog('error', '[AppError]', {
    code: e.code, domain: e.domain, severity: e.severity, message: e.message, ctx: e.ctx, cause: e.cause
  });

  if (e.code === ErrorCode.DB_PERMISSION_DENIED) {
    logPermissionDeniedDebug(e);
  }

  // TODO: behind env flag, send to Sentry or Firestore (throttled)
  const now = Date.now();
  if (now - lastSent < 3000) return;
  lastSent = now;
  // fireAndForget(e);
}

// Optional: Firestore reporter for error tracking
export async function reportToFirestore(err: AppError) {
  try {
    // Only report in production or when explicitly enabled
    if (process.env.NODE_ENV !== 'production' && !process.env.VITE_ENABLE_REMOTE_ERROR_LOGS) {
      return;
    }

    // Import Firebase only when needed
    const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
    const { db } = await import('../../services/firebaseConfig');

    const errorReport = {
      timestamp: serverTimestamp(),
      code: err.code,
      domain: err.domain,
      message: sanitizeData(err.message),
      context: sanitizeData(err.ctx),
      cause: err.cause ? sanitizeData(String(err.cause)) : null,
      stack: sanitizeData(err.stack),
      url: typeof window !== 'undefined' ? sanitizeData(window.location.href) : 'server',
      userAgent: typeof navigator !== 'undefined' ? sanitizeData(navigator.userAgent) : 'server'
    };

    // Add to errorReports collection with rate limiting
    const reportId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    await setDoc(doc(db, 'errorReports', reportId), errorReport);

    // Add reportId to context for correlation
    if (err.ctx) {
      err.ctx.extra = { ...err.ctx.extra, reportId };
    }

  } catch (reportError) {
    // Don't let reporting errors break the app
    secureLog('warn', 'Failed to report error to Firestore:', reportError);
  }
}

// Export secure logging functions for use throughout the app
export { secureLog, sanitizeData };
